{abs, ceil, floor, max, min, PI, random, round, sin} := Math

{
  BLEND_MODES
  Container
  SCALE_MODES
  Sprite
  Texture
} from "pixi.js"

{ JUDGMENT_FEEDBACK_MS } from ./const.civet

// The game-mode text treatment: chunky extruded arcade lettering with a
// glossy sheen, rendered once per label to a hi-res canvas so it stays crisp
// through the slam-in overshoot, plus the flame particles that ride hot
// judgments. Everything tunable lives in ARCADE_TUNING so the fx-lab page
// (dev/fx-lab.civet) can evolve the treatment live and read the settled
// numbers back into this file.

// Live-tunable treatment parameters, all relative to font size so one set of
// numbers serves the HUD's digits and the popups alike. fx-lab mutates these
// and calls clearArcadeCache(); shipped values are the settled defaults.
// Current values are the "modified" preset settled in fx-lab on 2026-08-28:
// a flat face with a hard lean and a strong shadow. Dials that tuning
// settled at fixed values live as constants below the tunables — fewer
// sliders, same look.
export ARCADE_TUNING := {
  skew: 0.32      // italic lean, tan of the angle
  sheen: 0.4      // gloss band alpha
  shadow: 0.75    // drop shadow ALPHA (opacity 0–1); its offset is fixed at
                  // 9% of font size — keeps the block legible on light fields
  overshoot: 3.4  // slam-in starting scale above rest
  popMs: 130      // slam-in duration
  wiggle: 0.12    // hot-tier wiggle amplitude (radians for flip, em-ish for shake)
  wiggleStyle: "punch" as "flip" | "shake" | "punch" | "none"
  flameRate: 0.6  // flame emission multiplier
  streaks: 2      // impact speed-line count multiplier
}

// Settled by tuning, no longer dials (the "modified" preset zeroed or never
// moved them). Promote one back into ARCADE_TUNING if it needs exploring.
EDGE := 2         // contour + extrusion: a hairline, in device px (was depth/outline: 0 with a 2px floor)
TOP_LIGHT := 0.7  // face gradient lightening toward white at top
SPACING := 0.005  // extra letter spacing
RISE_FRAC := 0.5  // portion of feedback life spent holding before the fade
WIGGLE_FALLBACK_MS := 70 // wiggle pose length with no transport (else 32nds)
FLAME_SIZE := 1   // flame particle size multiplier
SMEAR := 1        // slam-in smear-frame stretch amount

// --- beat clock -------------------------------------------------------------

// The transport feeds the current song beat here once per frame (renderer's
// master render; fx-lab fakes one from its bpm control). Cyclic animation
// clocks — wiggle flips, flame frames, the HUD's fluid slosh — quantize to
// musical subdivisions so every pose snap lands on the grid. Without a
// transport, the wall-clock fallback cadence applies.
let transportBeat: number? = undefined
export setArcadeBeat := (beat?: number) -> transportBeat = beat

// Pose index on a `sub`-per-beat grid (8 = 32nd notes, 16 = 64ths), falling
// back to a fixed ms cadence when no beat is running
export beatPose := (nowMs: number, sub: number, fallbackMs: number) ->
  if transportBeat?
    floor transportBeat * sub
  else
    floor nowMs / fallbackMs

// Chunky and ubiquitous; Fira Sans 900 catches the fallback where Arial
// Black is missing (Linux)
FONT_STACK := `"Arial Black", "Archivo Black", "Fira Sans", sans-serif`

// Textures render at 3× the nominal size: the slam-in shows them scaled well
// past 1 and they have to stay crisp through it
RES := 3

export mixColor := (a: number, b: number, t: number) ->
  ar := a >> 16 & 255
  ag := a >> 8 & 255
  ab := a & 255
  br := b >> 16 & 255
  bg := b >> 8 & 255
  bb := b & 255
  (round(ar + (br - ar) * t) << 16) | (round(ag + (bg - ag) * t) << 8) | round(ab + (bb - ab) * t)

cssColor := (c: number) -> `#${c.toString(16).padStart 6, "0"}`

// Text fades tick through 4 discrete alpha levels (0, ⅓, ⅔, 1) rather than
// sliding — and the levels are gamma-spaced so the steps read evenly to the
// eye (linear alpha steps bunch up perceptually at the bright end)
export quantAlpha := (a: number) ->
  (ceil(min(1, max 0, a) * 3) / 3) ** 1.8

textureCache := new Map<string, Texture>

// Drop the cache so the next lookup re-renders with current ARCADE_TUNING.
// The old textures are NOT destroyed — pooled popup sprites and the HUD may
// still be showing them this frame, and a destroyed texture crashes the
// renderer. Orphans linger until Pixi's TextureGC unloads them; this is a
// dev-page (fx-lab) path, not something the app hits in play.
export clearArcadeCache := ->
  textureCache.clear()

// One chunky label as a texture: dark contour, 3D extrusion, top-lit face
// gradient in the judgment color, then a white gloss band filled through the
// glyphs themselves (a mostly-transparent gradient fill — sheen with no
// clipping tricks). Cached per label/size/color.
export arcadeTextTexture := (text: string, fontSize: number, color: number) ->
  key := `${text}|${fontSize}|${color}`
  cached := textureCache.get key
  return cached if cached

  {skew, sheen, shadow} := ARCADE_TUNING
  px := fontSize * RES
  depth := EDGE
  outline := EDGE
  label := text.toUpperCase()
  font := `900 ${px}px ${FONT_STACK}`

  canvas := document.createElement "canvas"
  ctx := canvas.getContext("2d")!
  setFont := ->
    ctx.font = font
    // letterSpacing needs Chrome 99+; harmless where unsupported
    try (ctx as any).letterSpacing = `${round px * SPACING}px`
  setFont()
  m := ctx.measureText label
  ascent := ceil m.actualBoundingBoxAscent ?? px * 0.8
  descent := ceil m.actualBoundingBoxDescent ?? px * 0.2
  w := ceil m.width
  shadowDrop := round px * 0.09
  padX := outline + ceil((ascent + descent) * skew) + RES * 2
  padY := outline + RES * 2
  canvas.width = w + padX * 2
  canvas.height = ascent + descent + depth + padY * 2 + shadowDrop

  // the resize reset the context
  setFont()
  ctx.lineJoin = "round"
  ctx.textBaseline = "alphabetic"
  x := padX
  y := padY + ascent
  // italic lean, compensated so the baseline stays put
  ctx.setTransform 1, 0, -skew, 1, skew * y, 0

  // hard drop shadow under the whole block, so it holds on light fields.
  // Crisp by design (no blur, matching the app's crisp-over-blurry rule): the
  // full block silhouette — extrusion and contour included — is built opaque
  // on an offscreen canvas, then stamped down-shifted at uniform alpha.
  // (Overlapping translucent fills directly would double-darken.)
  if shadow > 0
    sc := document.createElement "canvas"
    sc.width = canvas.width
    sc.height = canvas.height
    sctx := sc.getContext("2d")!
    sctx.font = font
    try (sctx as any).letterSpacing = `${round px * SPACING}px`
    sctx.textBaseline = "alphabetic"
    sctx.lineJoin = "round"
    sctx.setTransform 1, 0, -skew, 1, skew * y, 0
    sctx.fillStyle = sctx.strokeStyle = "#000"
    sctx.lineWidth = outline * 2
    for d of [depth, 0]
      sctx.strokeText label, x, y + d
      sctx.fillText label, x, y + d
    ctx.save()
    ctx.setTransform 1, 0, 0, 1, 0, 0
    ctx.globalAlpha = shadow
    ctx.drawImage sc, 0, shadowDrop
    ctx.restore()

  // dark contour around the whole extruded block
  ctx.strokeStyle = cssColor mixColor color, 0x000000, 0.85
  ctx.lineWidth = outline * 2
  for d of [depth..0]
    ctx.strokeText label, x, y + d

  // extrusion body, darker toward the bottom
  side := ctx.createLinearGradient 0, y - ascent, 0, y + descent + depth
  side.addColorStop 0, cssColor mixColor color, 0x000000, 0.45
  side.addColorStop 1, cssColor mixColor color, 0x000000, 0.7
  ctx.fillStyle = side
  for d of [depth..1]
    ctx.fillText label, x, y + d

  // top face: lit from above, saturated at the bottom
  face := ctx.createLinearGradient 0, y - ascent, 0, y + descent
  face.addColorStop 0, cssColor mixColor color, 0xFFFFFF, TOP_LIGHT
  face.addColorStop 0.38, cssColor mixColor color, 0xFFFFFF, TOP_LIGHT * 0.45
  face.addColorStop 0.55, cssColor color
  face.addColorStop 1, cssColor mixColor color, 0x000000, 0.18
  ctx.fillStyle = face
  ctx.fillText label, x, y

  // sheen: a gloss band + top edge light, filled through the glyphs
  gloss := ctx.createLinearGradient 0, y - ascent, 0, y + descent
  gloss.addColorStop 0, `rgba(255,255,255,${0.9 * sheen})`
  gloss.addColorStop 0.12, "rgba(255,255,255,0)"
  gloss.addColorStop 0.34, "rgba(255,255,255,0)"
  gloss.addColorStop 0.38, `rgba(255,255,255,${0.75 * sheen})`
  gloss.addColorStop 0.5, `rgba(255,255,255,${0.5 * sheen})`
  gloss.addColorStop 0.54, "rgba(255,255,255,0)"
  ctx.fillStyle = gloss
  ctx.fillText label, x, y

  t := Texture.from canvas, {resolution: RES}
  // logical padding either side of the glyphs — ArcadeDigits subtracts it to
  // set letter advance from ink width, not canvas width — and the baseline's
  // logical y, so glyphs of different heights (comma!) can baseline-align
  (t as any).arcadePad = padX / RES
  (t as any).arcadeBaseline = (padY + ascent) / RES
  textureCache.set key, t
  t

// A row of per-glyph sprites for text that changes every frame (the HUD's
// score and combo counts). Whole-string textures would grow the cache without
// bound; glyphs are a closed set. set() lays the string out and returns its
// width in local units.
export ArcadeDigits := (parent: Container, {fontSize, color: initialColor}: {fontSize: number, color: number}) ->
  container := new Container
  parent.addChild container
  sprites: Sprite[] := []
  color .= initialColor

  setColor := (c: number) -> color = c

  set := (text: string) ->
    x .= 0
    // all glyphs hang from a shared baseline ("0" as the reference); without
    // this, short glyphs like the comma sit top-aligned and float up
    refBaseline := (arcadeTextTexture("0", fontSize, color) as any).arcadeBaseline as number
    chars := [...text]
    chars.forEach (ch, i) ->
      s .= sprites[i]
      unless s
        s = new Sprite
        sprites.push s
        container.addChild s
      s.texture = arcadeTextTexture ch, fontSize, color
      s.visible = true
      s.x = x
      s.y = refBaseline - ((s.texture as any).arcadeBaseline ?? refBaseline)
      // advance by ink width, not canvas width: the padding either side would
      // read as letter-spacing. A slight tuck fuses the outlines into a block.
      pad := ((s.texture as any).arcadePad ?? 0) * 2
      x += s.width - pad + fontSize * 0.04
    for j of [chars.length...sprites.length]
      sprites[j].visible = false
    x

  {container, set, setColor}

// --- flames ---------------------------------------------------------------

// Pixel-art flame frames with a baked, dithered gradient: '#' core, '+' mid,
// '-' rim, mixed at the boundaries so the shading dithers instead of banding.
// Five tip variants cycle to animate the lick — variety comes from frames,
// NEVER from rotating the sprite: small-angle rotation turns crisp pixels to
// mush. Colors bake per heat stage below (tint can't do multi-color).
FLAME_FRAMES := [
  [
    "....-..."
    "...-+..."
    "...+#-.."
    "..-+#+.."
    "..+###-."
    ".-+###+."
    ".-#+##-."
    "..+###.."
    "..-+#+.."
    "...-+-.."
  ]
  [
    "..-....."
    "..-+...."
    "..+#-..."
    ".-+#+-.."
    ".-+###-."
    ".-#+##+."
    "..+###-."
    "..-+#+.."
    "...-+-.."
    "....-..."
  ]
  [
    ".....-.."
    "....+-.."
    "...-#+.."
    "..-+#+-."
    ".-###+-."
    ".+##+#-."
    ".-###+.."
    "..+#+-.."
    "..-+-..."
    "...-...."
  ]
  [
    "...-...."
    "...+-..."
    "..-#+..."
    "..+#+-.."
    ".-###+.."
    ".+#+##-."
    ".-####-."
    "..+##+.."
    "..-+#-.."
    "...+-..."
  ]
  [
    "....-..."
    "....+-.."
    "...+#-.."
    "..-+##.."
    "..+#+#-."
    ".-##+#+."
    "..+###-."
    "..-###.."
    "...+#-.."
    "...-+..."
  ]
]

// hottest → coolest; a particle walks down these as it burns out
FLAME_STAGES := [
  {"#": "#FFFFFF", "+": "#FFF9C4", "-": "#FFD54F"}
  {"#": "#FFF9C4", "+": "#FFD54F", "-": "#FF9130"}
  {"#": "#FFD54F", "+": "#FF9130", "-": "#F4511E"}
  {"#": "#FF9130", "+": "#F4511E", "-": "#B71C1C"}
]

// textures[stage][frame]
let flameTextures: Texture[][]?
makeFlameTextures := ->
  FLAME_STAGES.map (palette) ->
    FLAME_FRAMES.map (rows) ->
      canvas := document.createElement "canvas"
      canvas.width = rows[0].length
      canvas.height = rows.length
      ctx := canvas.getContext("2d")!
      rows.forEach (row, y) ->
        for x of [0...row.length]
          color := (palette as Record<string, string>)[row[x]]
          continue unless color
          ctx.fillStyle = color
          ctx.fillRect x, y, 1, 1
      Texture.from canvas, {scaleMode: SCALE_MODES.NEAREST}

// the 2×2 pixel a dying flame dissolves into
let emberTexture: Texture?
makeEmberTexture := ->
  canvas := document.createElement "canvas"
  canvas.width = canvas.height = 2
  ctx := canvas.getContext("2d")!
  ctx.fillStyle = "#ffffff"
  ctx.fillRect 0, 0, 2, 2
  Texture.from canvas, {scaleMode: SCALE_MODES.NEAREST}

EMBER_COLORS := [0xFFD54F, 0xFF9130, 0xF4511E]

type Flame =
  sprite: Sprite
  x: number
  y: number
  vx: number
  vy: number
  born: number
  life: number
  size: number
  sway: number
  frame: number

// Fire licking up from a hot judgment: white-yellow at birth, cooling
// through orange to red as each tongue rises and shrinks. Additive on the
// rolls' dark field; the staff passes false and gets opaque embers.
export FlameLayer := (parent: Container, {cap = 64, additive = true} = {}) ->
  layer := new Container
  parent.addChild layer
  pool: Sprite[] := []
  active: Flame[] := []
  emberPool: Sprite[] := []
  embers: Streak[] := [] // same shape: position, velocity, born, life
  // the last clock step() ran on; births stamp from it so ages never mix a
  // wall-time birth with a synthetic-clock step (offline renders)
  layerNow .= undefined as number?

  emit := (x: number, y: number, scale = 1) ->
    s .= pool.pop()
    unless s
      return if layer.children.length >= cap
      flameTextures ?= makeFlameTextures()
      s = new Sprite flameTextures[0][0]
      s.anchor.set 0.5, 0.8
      s.blendMode = if additive then BLEND_MODES.ADD else BLEND_MODES.NORMAL
      layer.addChild s
    s.visible = true
    active.push {
      sprite: s
      x, y
      vx: (random() - 0.5) * 24
      vy: -(60 + random() * 70) * scale
      born: layerNow ?? performance.now()
      life: 0.22 + random() * 0.26
      size: (1.5 + random() * 1.3) * scale
      sway: (random() - 0.5) * 12
      frame: floor random() * FLAME_FRAMES.length
    }
    return

  // a dying flame breaks apart into scattering pixel embers — dissolution by
  // particles, not by fading through translucency
  dissolve := (p: Flame, now: number) ->
    age := p.life
    fx := p.x + p.vx * age + p.sway * sin age * 9
    fy := p.y + p.vy * age
    n := 3 + floor random() * 3
    for k of [0...n]
      s .= emberPool.pop()
      unless s
        break if layer.children.length >= cap * 2
        emberTexture ?= makeEmberTexture()
        s = new Sprite emberTexture
        s.anchor.set 0.5
        s.blendMode = if additive then BLEND_MODES.ADD else BLEND_MODES.NORMAL
        layer.addChild s
      s.visible = true
      s.tint = EMBER_COLORS[floor random() * EMBER_COLORS.length]
      s.scale.set p.size * (0.5 + random() * 0.5)
      embers.push {
        sprite: s
        x: fx + (random() - 0.5) * p.size * 5
        y: fy - random() * p.size * 6
        vx: (random() - 0.5) * 110
        vy: p.vy * 0.4 - random() * 50
        born: now
        life: 0.1 + random() * 0.2
      }
    return

  step := (now: number) ->
    layerNow = now
    i .= 0
    while i < active.length
      p := active[i]
      // a flame emitted later in this same frame has born > now; clamping
      // keeps age (and so heat, and the stage index) in range
      age := max 0, (now - p.born) / 1000
      if age >= p.life
        dissolve p, now
        p.sprite.visible = false
        pool.push p.sprite
        active[i] = active[active.length - 1]
        active.pop()
        continue
      heat := 1 - age / p.life // 1 at birth, 0 burnt out
      {sprite} := p
      // motion runs at full framerate; only the pixel frame steps — on the
      // 64th-note grid when the transport runs, ~12fps otherwise, like
      // classic sprite-sheet fire. Never rotated: crisp pixels stay square.
      stage := min FLAME_STAGES.length - 1, floor (1 - heat) * FLAME_STAGES.length
      sprite.texture = flameTextures![stage][(p.frame + beatPose(now, 16, 84)) % FLAME_FRAMES.length]
      sprite.x = p.x + p.vx * age + p.sway * sin age * 9
      sprite.y = p.y + p.vy * age
      // fully solid its whole life — death is the ember dissolve, not a fade
      sprite.alpha = if additive then 1 else 0.95
      sprite.scale.set p.size * (0.55 + 0.45 * heat)
      i++

    j .= 0
    while j < embers.length
      e := embers[j]
      age := (now - e.born) / 1000
      if age >= e.life
        e.sprite.visible = false
        emberPool.push e.sprite
        embers[j] = embers[embers.length - 1]
        embers.pop()
        continue
      e.sprite.x = e.x + e.vx * age
      e.sprite.y = e.y + e.vy * age
      e.sprite.alpha = if additive then 1 else 0.95
      j++
    return

  {emit, step}

// --- speed-line streaks ----------------------------------------------------

// A 1px-tall pixel bar, stretched along its motion: the crisp impact streak.
// Solid its whole life, then gone — no fade tail.
let streakTexture: Texture?
makeStreakTexture := ->
  canvas := document.createElement "canvas"
  canvas.width = 8
  canvas.height = 1
  ctx := canvas.getContext("2d")!
  ctx.fillStyle = "#ffffff"
  ctx.fillRect 0, 0, 8, 1
  Texture.from canvas, {scaleMode: SCALE_MODES.NEAREST}

type Streak =
  sprite: Sprite
  x: number
  y: number
  vx: number
  vy: number
  born: number
  life: number

export StreakLayer := (parent: Container, {cap = 32} = {}) ->
  layer := new Container
  parent.addChild layer
  pool: Sprite[] := []
  active: Streak[] := []
  // see FlameLayer's layerNow — same clock discipline
  layerNow .= undefined as number?

  emit := (x: number, y: number, vx: number, vy: number, color: number, scale = 1) ->
    s .= pool.pop()
    unless s
      return if layer.children.length >= cap
      streakTexture ?= makeStreakTexture()
      s = new Sprite streakTexture
      s.anchor.set 0.5
      layer.addChild s
    s.visible = true
    s.tint = color
    // stretched along the motion axis, a couple px thick; axis-aligned only —
    // arbitrary angles would break the pixel grid
    s.scale.set (4 + random() * 4) * scale, 2 * scale
    s.rotation = if abs(vy) > abs(vx) then PI / 2 else 0
    active.push {
      sprite: s
      x, y, vx, vy
      born: layerNow ?? performance.now()
      life: 0.09 + random() * 0.07
    }
    return

  step := (now: number) ->
    layerNow = now
    i .= 0
    while i < active.length
      p := active[i]
      age := (now - p.born) / 1000
      if age >= p.life
        p.sprite.visible = false
        pool.push p.sprite
        active[i] = active[active.length - 1]
        active.pop()
        continue
      p.sprite.x = p.x + p.vx * age
      p.sprite.y = p.y + p.vy * age
      // solid, then gone — snap out at 75% life
      p.sprite.alpha = if age / p.life < 0.75 then 1 else 0
      i++
    return

  {emit, step}

// --- judgment popups -------------------------------------------------------

// What burns: perfect always, anything correct once the multiplier runs hot
flamesFor := (judgment: string, tier: number) ->
  return 2 if judgment is "perfect" and tier >= 3
  return 1.4 if judgment is "perfect"
  return 1 if tier >= 3 and judgment is not "miss" and judgment is not "wrong"
  0

// The big arcade judgment popup layer every view shares, superseding the
// plain TextSpritePool popups. Call pop() for each live feedback entry every
// frame, then finish(now) once per frame to hide spares and step the flames.
//
// Animation: slam in from overshoot, settle with a bounce, hold crisp at
// rest scale, rise, and only fade in the tail — no long blurry crossfade.
export JudgmentFx := (parent: Container, {fontSize = 34, light = false, cap = 8} = {}) ->
  layer := new Container
  parent.addChild layer
  flames := FlameLayer layer, additive: !light
  speedLines := StreakLayer layer
  sprites: Sprite[] := []
  i .= 0

  pop := (text: string, color: number, x: number, y: number, age: number, {tier = 1, scale = 1, anchorX = 0.5, judgment}: {tier?: number, scale?: number, anchorX?: number, judgment?: string} = {}) ->
    return if i >= cap
    s .= sprites[i]
    unless s
      s = new Sprite
      sprites.push s
      layer.addChild s
    i++
    s.texture = arcadeTextTexture text, fontSize, color
    s.anchor.set anchorX, 0.5
    s.visible = true

    {overshoot, popMs, wiggle, wiggleStyle, streaks} := ARCADE_TUNING
    life := min 1, age / JUDGMENT_FEEDBACK_MS
    t := min 1, age / popMs
    // slam: starts past rest scale and crashes down…
    slam := 1 + overshoot * (1 - t) ** 2
    // …then a hard undershoot snap on landing — crisp poses, no smooth bounce
    settle :=
      if age <= popMs then 1
      else if age <= popMs + 80 then 0.94
      else 1
    sc .= scale * slam * settle
    // hot tiers wiggle in the configured style, all square-wave pose snaps
    // riding the 32nd-note grid — compare styles in fx-lab's anim preview
    flip := if beatPose(performance.now(), 8, WIGGLE_FALLBACK_MS) % 2 then 1 else -1
    shakeX .= 0
    s.rotation = 0
    if tier >= 3 and life < 0.6
      switch wiggleStyle
        when "flip" then s.rotation = wiggle * flip
        when "shake" then shakeX = wiggle * fontSize * 0.8 * flip
        when "punch" then sc *= 1 + wiggle * 0.6 * (if flip > 0 then 1 else 0)
    if t < 1
      // smear frame: stretched tall while crashing in, snapping square on land
      stretch := SMEAR * 0.8 * (1 - t) ** 2
      s.scale.set sc * (1 - 0.25 * stretch), sc * (1 + stretch)
    else
      s.scale.set sc
    rise := 30 * max(0, (life - RISE_FRAC) / (1 - RISE_FRAC)) ** 0.9
    s.x = x + shakeX
    s.y = y - rise

    // the frame the slam lands, speed lines shoot out sideways along the
    // baseline (lastAge rides the pooled sprite to detect the crossing)
    prevAge := (s as any).lastAge ?? Infinity
    (s as any).lastAge = age
    if streaks > 0 and prevAge < popMs and age >= popMs
      streakColor := if light then color else 0xFFFFFF
      cx := x + (0.5 - anchorX) * s.width
      n := max 1, round 2 * streaks
      for k of [0...n]
        sp := (350 + random() * 400) * scale
        yy := y - rise + (random() - 0.5) * fontSize * scale * 0.7
        speedLines.emit cx - s.width * 0.45, yy, -sp, 0, streakColor, scale
        speedLines.emit cx + s.width * 0.45, yy, sp, 0, streakColor, scale
    s.alpha = if life > 0.72 then quantAlpha (1 - life) / 0.28 else 1

    // flames lick off the top edge of the letters while the popup is fresh —
    // throttled by chance per frame so the fire flickers instead of clouding
    fl := flamesFor judgment ?? text.toLowerCase(), tier
    if fl > 0 and life < 0.5
      {flameRate} := ARCADE_TUNING
      top := y - rise - s.height * 0.3
      chance := 0.55 * fl * flameRate
      if random() < chance
        flames.emit x + (random() - anchorX) * s.width * 0.9, top, fl * scale * 0.8 * FLAME_SIZE
      if chance > 1 and random() < chance - 1
        flames.emit x + (random() - anchorX) * s.width * 0.9, top, fl * scale * 0.7 * FLAME_SIZE
      if tier >= 3 and random() < 0.4 * flameRate
        flames.emit x + (random() - anchorX) * s.width * 0.9, top, fl * scale * 0.6 * FLAME_SIZE
    return

  finish := (now: number) ->
    while i < sprites.length
      sprites[i++].visible = false
    i = 0
    flames.step now
    speedLines.step now

  {pop, finish}

export default JudgmentFx
